/**
 * This file was auto-generated.
 * Do not make direct changes to the file.
 */
/* eslint-disable @typescript-eslint/no-unused-vars */

import Axios, { AxiosError, AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios'
import { z } from 'zod'

import {
	Endpoint,
	EndpointInput,
	HttpMethod,
	EndpointRequestValidators,
	Paths,
	Methods,
	EndpointResponse,
	EndpointNoThrowResult
} from './common'
export interface RequestInput {
	path?: Record<string, any>
	query?: Record<string, any>
	body?: any
}

export function splitQueryParamValue<T = string>(value: string | undefined): T[] {
	return (value ?? '')
		.split(',')
		.filter(x => !!x)
		.map(x => x.trim()) as T[]
}

export function joinQueryParamValue(values: string[] | undefined): string {
	return values?.filter(x => !!x).join(',') ?? ''
}

export function getAllRegexMatches(text: string, regex: RegExp): string[] {
	const result: string[] = []
	let matches: RegExpExecArray | null
	while ((matches = regex.exec(text))) {
		result.push(matches[1])
	}
	return result
}

export function pruneQuery(query: any) {
	if (!query) {
		return {}
	}
	const pruned: any = {}
	for (const [key, value] of Object.entries(query)) {
		if (value !== undefined) {
			pruned[key] = value
		}
	}
	return pruned
}

export function createUrl(path: string, input: any) {
	if (!input) {
		return path
	}
	if (input.path) {
		for (const [key, value] of Object.entries(input.path)) {
			path = path.replace(`:${key}`, value as string)
		}
	}
	const query = pruneQuery(input.query)
	if (Object.entries(query).length > 0) {
		const valuePairs = Object.entries(query).map(
			([key, value]) => `${key}=${encodeURIComponent((Array.isArray(value) ? value : [value]).join(','))}`
		)
		path += `?${valuePairs.join('&')}`
	}
	return path
}

export function validateUrl(endpoint: any, path: string, request: any) {
	try {
		const pathInput = { ...(request?.path ?? {}) }
		const queryInput = { ...(request?.query ?? {}) }
		const rawUrlParameters = { ...pathInput, ...pruneQuery(queryInput) }
		const urlParameters: any = {}
		for (const [key, value] of Object.entries(rawUrlParameters)) {
			if (value !== undefined) {
				urlParameters[key] = value
			}
		}
		const notFoundValues = Object.entries({ ...pathInput }).filter(
			([key, value]) => key !== 'storeId' && path.indexOf(`:${key}`) < 0
		)
		if (notFoundValues.length > 0) {
			throw new Error(
				`${endpoint}: Url parameters not found in url template: ${notFoundValues.map(([key]) => key).join(', ')}`
			)
		}

		const parameterRegex = /:(.*?)(\/|$|\?|&)/g
		const parameterMatches = getAllRegexMatches(path, parameterRegex)

		const unaccountedUrlParameters = parameterMatches.filter(
			parameterName => urlParameters[parameterName] === undefined
		)
		if (unaccountedUrlParameters.length > 0) {
			throw new Error(
				`${endpoint}: Url parameters found in url but not in parameter object: ${unaccountedUrlParameters
					.map(x => `:${x}`)
					.join(', ')}`
			)
		}
	} catch (e: any) {
		console.error(e)
		throw e
	}
}

export class SentryTag {
	constructor(
		public key: string,
		public value: string
	) {}
}

export const isApiError = (e: any): e is ApiError => {
	return e._isApiError
}

export class ApiError extends Error {
	readonly _isApiError = true
	title?: string
	httpStatusCode = 0
	errorType = ''
	data: any
	endpoint = ''
	extras: any = {}
	tags: SentryTag[] = []

	addExtras(input: any) {
		this.extras = { ...this.extras, ...input }
		return this
	}

	addTag(...tags: SentryTag[]) {
		this.tags.push(...tags)
		return this
	}

	static from(endpoint: string, e: AxiosError, stack?: string) {
		const error = new ApiError(`Error in endpoint: ${endpoint}`)
		if (stack) {
			error.stack = stack
		}
		error.endpoint = endpoint
		error.addTag(new SentryTag('endpoint', endpoint))
		if (e.response) {
			error.httpStatusCode = e.response?.status
			error.errorType = (e.response?.data as any)?.errorType
			error.data = e.response?.data
		}
		return error
	}
}

export type HttpMethodType = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

export type MakeRequestOptions = {
	throwOnError?: boolean
}

export class BaseApi {
	constructor(public client: AxiosInstance) {}

	protected async checkIfEndpointNeedsToWait(endpoint: string): Promise<void> {
		return
	}

	protected async internalMakeRequest<TInput, TResponse>(
		input: TInput,
		endpoint: string,
		method: HttpMethodType,
		path: string,
		options?: MakeRequestOptions
	) {
		validateUrl(endpoint, path, input)
		const substitutedPath = createUrl(path, input)

		let result: AxiosResponse<TResponse> | undefined = undefined

		await this.checkIfEndpointNeedsToWait(endpoint)

		const requestConfig: AxiosRequestConfig = {
			validateStatus: status => {
				if (options?.throwOnError) {
					return status >= 200 && status < 300
				}
				return true
			},
			headers: {
				...(!(input as any)?.body
					? {
							'Content-Type': 'text/plain'
						}
					: {}),
				...((input as any)?.headers ?? {})
			}
		}
		switch (method) {
			case 'GET':
				result = await this.client.get<TResponse>(substitutedPath, requestConfig)
				break
			case 'POST':
				result = await this.client.post<TResponse>(substitutedPath, (input as any)?.body, requestConfig)
				break
			case 'PUT':
				result = await this.client.put<TResponse>(substitutedPath, (input as any)?.body, requestConfig)
				break
			case 'DELETE':
				result = await this.client.delete<TResponse>(substitutedPath, requestConfig)
				break
			default:
				throw new Error(`Unsupported method: ${method}`)
		}
		if (!result) {
			throw new Error(`No result from ${method} ${substitutedPath}`)
		}
		return { status: result?.status, data: result?.data, raw: result }
	}
}

export class Api extends BaseApi {
	protected createUrl<T extends Endpoint>(endpoint: T, input: EndpointInput<T>) {
		const path = Paths[endpoint]
		validateUrl(endpoint, path, input)
		return createUrl(path, input)
	}

	protected async onBeforeRequest<T extends Endpoint>(data: {
		endpoint: T
		input: any
		method: HttpMethod
		path: string
	}) {}

	protected async onAfterResponse<T extends Endpoint>(
		data: { endpoint: T; input: any; method: HttpMethod; path: string },
		response: { status: number; data: EndpointResponse<T> }
	) {}

	async makeRequest<T extends Endpoint>(endpoint: T, input: EndpointInput<T>, options?: MakeRequestOptions) {
		const stackTrace = new Error().stack
		try {
			const parseResult = EndpointRequestValidators[endpoint].input.safeParse(input)
			if (!parseResult.success) {
				console.error(`Invalid input for ${endpoint}`, parseResult.error.format())
				if (options?.throwOnError) {
					throw parseResult.error
				}
				return {
					isSuccess: false,
					status: 400,
					data: parseResult.error.format() as unknown as EndpointResponse<T>,
					raw: undefined
				}
			}
			const parsed = parseResult.data
			const path = Paths[endpoint]
			const method = Methods[endpoint] as HttpMethod
			await this.onBeforeRequest({ endpoint, input: parsed, method, path })
			const result = await this.internalMakeRequest<EndpointInput<T>, EndpointResponse<T>>(
				parsed as EndpointInput<T>,
				endpoint,
				method,
				path,
				options
			)
			await this.onAfterResponse({ endpoint, input: parsed, method, path }, result)
			if (!result) {
				throw new Error('Received undefined response')
			}
			return result
		} catch (e) {
			if (Axios.isAxiosError(e)) {
				const error = ApiError.from(endpoint, e, stackTrace)
				error.addExtras({
					axiosCode: e.code,
					cause: e.cause,
					request: JSON.stringify(input, null, 2),
					requestId: e.config?.headers['request-id'],
					baseUrl: e.config?.baseURL,
					url: e.config?.url
				})
				if (e.response) {
					const response = e.response.data
					error.addExtras({
						response: JSON.stringify(response ?? '', null, 2),
						request: JSON.stringify(input, null, 2),
						requestId: e.config?.headers['request-id'],
						headers: e.response.headers,
						statusCode: e.response.status,
						url: e.config?.url
					})
				}
				console.error(error.message, JSON.stringify(error, null, 2))
				throw error
			} else {
				throw e
			}
		}
		throw new Error('Request failed for unknown reason, should never happen')
	}

	async makeRequestWithThrow<T extends Endpoint>(endpoint: T, input: EndpointInput<T>) {
		const result = await this.makeRequest(endpoint, input, { throwOnError: true })
		return result.data
	}
	async makeRequestNoThrow<T extends Endpoint>(
		endpoint: T,
		input: EndpointInput<T>
	): Promise<EndpointNoThrowResult<T>> {
		try {
			const result = await this.makeRequest(endpoint, input, { throwOnError: false })
			if (result.status >= 200 && result.status < 300) {
				return { isSuccess: true, status: result.status, response: result.data, raw: result.raw } as any
			} else {
				return { isSuccess: false, status: result.status, response: result.data, raw: result.raw } as any
			}
		} catch (e: any) {
			return { isSuccess: false, status: 0, response: e?.data as any, error: e } as any
		}
	}

	async getVersionInfo(input: EndpointInput<Endpoint.GetVersionInfo>) {
		return this.makeRequestWithThrow(Endpoint.GetVersionInfo, input)
	}
	async getVersionInfoNoThrow(input: EndpointInput<Endpoint.GetVersionInfo>) {
		return this.makeRequestNoThrow(Endpoint.GetVersionInfo, input)
	}

	async getFeatures(input: EndpointInput<Endpoint.GetFeatures>) {
		return this.makeRequestWithThrow(Endpoint.GetFeatures, input)
	}
	async getFeaturesNoThrow(input: EndpointInput<Endpoint.GetFeatures>) {
		return this.makeRequestNoThrow(Endpoint.GetFeatures, input)
	}

	async customerLogin(input: EndpointInput<Endpoint.CustomerLogin>) {
		return this.makeRequestWithThrow(Endpoint.CustomerLogin, input)
	}
	async customerLoginNoThrow(input: EndpointInput<Endpoint.CustomerLogin>) {
		return this.makeRequestNoThrow(Endpoint.CustomerLogin, input)
	}

	async verifyBusinessCardPin(input: EndpointInput<Endpoint.VerifyBusinessCardPin>) {
		return this.makeRequestWithThrow(Endpoint.VerifyBusinessCardPin, input)
	}
	async verifyBusinessCardPinNoThrow(input: EndpointInput<Endpoint.VerifyBusinessCardPin>) {
		return this.makeRequestNoThrow(Endpoint.VerifyBusinessCardPin, input)
	}

	async getCustomer(input: EndpointInput<Endpoint.GetCustomer>) {
		return this.makeRequestWithThrow(Endpoint.GetCustomer, input)
	}
	async getCustomerNoThrow(input: EndpointInput<Endpoint.GetCustomer>) {
		return this.makeRequestNoThrow(Endpoint.GetCustomer, input)
	}

	async updateCustomer(input: EndpointInput<Endpoint.UpdateCustomer>) {
		return this.makeRequestWithThrow(Endpoint.UpdateCustomer, input)
	}
	async updateCustomerNoThrow(input: EndpointInput<Endpoint.UpdateCustomer>) {
		return this.makeRequestNoThrow(Endpoint.UpdateCustomer, input)
	}

	async acceptTerms(input: EndpointInput<Endpoint.AcceptTerms>) {
		return this.makeRequestWithThrow(Endpoint.AcceptTerms, input)
	}
	async acceptTermsNoThrow(input: EndpointInput<Endpoint.AcceptTerms>) {
		return this.makeRequestNoThrow(Endpoint.AcceptTerms, input)
	}

	async deleteAccount(input: EndpointInput<Endpoint.DeleteAccount>) {
		return this.makeRequestWithThrow(Endpoint.DeleteAccount, input)
	}
	async deleteAccountNoThrow(input: EndpointInput<Endpoint.DeleteAccount>) {
		return this.makeRequestNoThrow(Endpoint.DeleteAccount, input)
	}

	async cancelDeletion(input: EndpointInput<Endpoint.CancelDeletion>) {
		return this.makeRequestWithThrow(Endpoint.CancelDeletion, input)
	}
	async cancelDeletionNoThrow(input: EndpointInput<Endpoint.CancelDeletion>) {
		return this.makeRequestNoThrow(Endpoint.CancelDeletion, input)
	}

	async getCustomerOrders(input: EndpointInput<Endpoint.GetCustomerOrders>) {
		return this.makeRequestWithThrow(Endpoint.GetCustomerOrders, input)
	}
	async getCustomerOrdersNoThrow(input: EndpointInput<Endpoint.GetCustomerOrders>) {
		return this.makeRequestNoThrow(Endpoint.GetCustomerOrders, input)
	}

	async getCustomerOrdersGroupedByDate(input: EndpointInput<Endpoint.GetCustomerOrdersGroupedByDate>) {
		return this.makeRequestWithThrow(Endpoint.GetCustomerOrdersGroupedByDate, input)
	}
	async getCustomerOrdersGroupedByDateNoThrow(input: EndpointInput<Endpoint.GetCustomerOrdersGroupedByDate>) {
		return this.makeRequestNoThrow(Endpoint.GetCustomerOrdersGroupedByDate, input)
	}

	async taktikalAuthStart(input: EndpointInput<Endpoint.TaktikalAuthStart>) {
		return this.makeRequestWithThrow(Endpoint.TaktikalAuthStart, input)
	}
	async taktikalAuthStartNoThrow(input: EndpointInput<Endpoint.TaktikalAuthStart>) {
		return this.makeRequestNoThrow(Endpoint.TaktikalAuthStart, input)
	}

	async taktikalAuthPoll(input: EndpointInput<Endpoint.TaktikalAuthPoll>) {
		return this.makeRequestWithThrow(Endpoint.TaktikalAuthPoll, input)
	}
	async taktikalAuthPollNoThrow(input: EndpointInput<Endpoint.TaktikalAuthPoll>) {
		return this.makeRequestNoThrow(Endpoint.TaktikalAuthPoll, input)
	}

	async getLoyaltyCard(input: EndpointInput<Endpoint.GetLoyaltyCard>) {
		return this.makeRequestWithThrow(Endpoint.GetLoyaltyCard, input)
	}
	async getLoyaltyCardNoThrow(input: EndpointInput<Endpoint.GetLoyaltyCard>) {
		return this.makeRequestNoThrow(Endpoint.GetLoyaltyCard, input)
	}

	async getLoyaltyCardWalletLink(input: EndpointInput<Endpoint.GetLoyaltyCardWalletLink>) {
		return this.makeRequestWithThrow(Endpoint.GetLoyaltyCardWalletLink, input)
	}
	async getLoyaltyCardWalletLinkNoThrow(input: EndpointInput<Endpoint.GetLoyaltyCardWalletLink>) {
		return this.makeRequestNoThrow(Endpoint.GetLoyaltyCardWalletLink, input)
	}

	async joinLoyaltyProgram(input: EndpointInput<Endpoint.JoinLoyaltyProgram>) {
		return this.makeRequestWithThrow(Endpoint.JoinLoyaltyProgram, input)
	}
	async joinLoyaltyProgramNoThrow(input: EndpointInput<Endpoint.JoinLoyaltyProgram>) {
		return this.makeRequestNoThrow(Endpoint.JoinLoyaltyProgram, input)
	}

	async getLoyaltyCardHistory(input: EndpointInput<Endpoint.GetLoyaltyCardHistory>) {
		return this.makeRequestWithThrow(Endpoint.GetLoyaltyCardHistory, input)
	}
	async getLoyaltyCardHistoryNoThrow(input: EndpointInput<Endpoint.GetLoyaltyCardHistory>) {
		return this.makeRequestNoThrow(Endpoint.GetLoyaltyCardHistory, input)
	}

	async storeGetAppUpdateBranches(input: EndpointInput<Endpoint.StoreGetAppUpdateBranches>) {
		return this.makeRequestWithThrow(Endpoint.StoreGetAppUpdateBranches, input)
	}
	async storeGetAppUpdateBranchesNoThrow(input: EndpointInput<Endpoint.StoreGetAppUpdateBranches>) {
		return this.makeRequestNoThrow(Endpoint.StoreGetAppUpdateBranches, input)
	}

	async storeGetAppUpdates(input: EndpointInput<Endpoint.StoreGetAppUpdates>) {
		return this.makeRequestWithThrow(Endpoint.StoreGetAppUpdates, input)
	}
	async storeGetAppUpdatesNoThrow(input: EndpointInput<Endpoint.StoreGetAppUpdates>) {
		return this.makeRequestNoThrow(Endpoint.StoreGetAppUpdates, input)
	}

	async getPointOfSaleInfo(input: EndpointInput<Endpoint.GetPointOfSaleInfo>) {
		return this.makeRequestWithThrow(Endpoint.GetPointOfSaleInfo, input)
	}
	async getPointOfSaleInfoNoThrow(input: EndpointInput<Endpoint.GetPointOfSaleInfo>) {
		return this.makeRequestNoThrow(Endpoint.GetPointOfSaleInfo, input)
	}

	async createAuditLog(input: EndpointInput<Endpoint.CreateAuditLog>) {
		return this.makeRequestWithThrow(Endpoint.CreateAuditLog, input)
	}
	async createAuditLogNoThrow(input: EndpointInput<Endpoint.CreateAuditLog>) {
		return this.makeRequestNoThrow(Endpoint.CreateAuditLog, input)
	}

	async getPaymentProviders(input: EndpointInput<Endpoint.GetPaymentProviders>) {
		return this.makeRequestWithThrow(Endpoint.GetPaymentProviders, input)
	}
	async getPaymentProvidersNoThrow(input: EndpointInput<Endpoint.GetPaymentProviders>) {
		return this.makeRequestNoThrow(Endpoint.GetPaymentProviders, input)
	}

	async getPromotions(input: EndpointInput<Endpoint.GetPromotions>) {
		return this.makeRequestWithThrow(Endpoint.GetPromotions, input)
	}
	async getPromotionsNoThrow(input: EndpointInput<Endpoint.GetPromotions>) {
		return this.makeRequestNoThrow(Endpoint.GetPromotions, input)
	}

	async listSalesChannels(input: EndpointInput<Endpoint.ListSalesChannels>) {
		return this.makeRequestWithThrow(Endpoint.ListSalesChannels, input)
	}
	async listSalesChannelsNoThrow(input: EndpointInput<Endpoint.ListSalesChannels>) {
		return this.makeRequestNoThrow(Endpoint.ListSalesChannels, input)
	}

	async createOrder(input: EndpointInput<Endpoint.CreateOrder>) {
		return this.makeRequestWithThrow(Endpoint.CreateOrder, input)
	}
	async createOrderNoThrow(input: EndpointInput<Endpoint.CreateOrder>) {
		return this.makeRequestNoThrow(Endpoint.CreateOrder, input)
	}

	async getOrdersByIds(input: EndpointInput<Endpoint.GetOrdersByIds>) {
		return this.makeRequestWithThrow(Endpoint.GetOrdersByIds, input)
	}
	async getOrdersByIdsNoThrow(input: EndpointInput<Endpoint.GetOrdersByIds>) {
		return this.makeRequestNoThrow(Endpoint.GetOrdersByIds, input)
	}

	async getOrder(input: EndpointInput<Endpoint.GetOrder>) {
		return this.makeRequestWithThrow(Endpoint.GetOrder, input)
	}
	async getOrderNoThrow(input: EndpointInput<Endpoint.GetOrder>) {
		return this.makeRequestNoThrow(Endpoint.GetOrder, input)
	}

	async clearOrder(input: EndpointInput<Endpoint.ClearOrder>) {
		return this.makeRequestWithThrow(Endpoint.ClearOrder, input)
	}
	async clearOrderNoThrow(input: EndpointInput<Endpoint.ClearOrder>) {
		return this.makeRequestNoThrow(Endpoint.ClearOrder, input)
	}

	async addLineItem(input: EndpointInput<Endpoint.AddLineItem>) {
		return this.makeRequestWithThrow(Endpoint.AddLineItem, input)
	}
	async addLineItemNoThrow(input: EndpointInput<Endpoint.AddLineItem>) {
		return this.makeRequestNoThrow(Endpoint.AddLineItem, input)
	}

	async updateLineItem(input: EndpointInput<Endpoint.UpdateLineItem>) {
		return this.makeRequestWithThrow(Endpoint.UpdateLineItem, input)
	}
	async updateLineItemNoThrow(input: EndpointInput<Endpoint.UpdateLineItem>) {
		return this.makeRequestNoThrow(Endpoint.UpdateLineItem, input)
	}

	async deleteLineItem(input: EndpointInput<Endpoint.DeleteLineItem>) {
		return this.makeRequestWithThrow(Endpoint.DeleteLineItem, input)
	}
	async deleteLineItemNoThrow(input: EndpointInput<Endpoint.DeleteLineItem>) {
		return this.makeRequestNoThrow(Endpoint.DeleteLineItem, input)
	}

	async updateProductQuantity(input: EndpointInput<Endpoint.UpdateProductQuantity>) {
		return this.makeRequestWithThrow(Endpoint.UpdateProductQuantity, input)
	}
	async updateProductQuantityNoThrow(input: EndpointInput<Endpoint.UpdateProductQuantity>) {
		return this.makeRequestNoThrow(Endpoint.UpdateProductQuantity, input)
	}

	async removeGiftCard(input: EndpointInput<Endpoint.RemoveGiftCard>) {
		return this.makeRequestWithThrow(Endpoint.RemoveGiftCard, input)
	}
	async removeGiftCardNoThrow(input: EndpointInput<Endpoint.RemoveGiftCard>) {
		return this.makeRequestNoThrow(Endpoint.RemoveGiftCard, input)
	}

	async getBagCheckStatus(input: EndpointInput<Endpoint.GetBagCheckStatus>) {
		return this.makeRequestWithThrow(Endpoint.GetBagCheckStatus, input)
	}
	async getBagCheckStatusNoThrow(input: EndpointInput<Endpoint.GetBagCheckStatus>) {
		return this.makeRequestNoThrow(Endpoint.GetBagCheckStatus, input)
	}

	async submitPaymentInfo(input: EndpointInput<Endpoint.SubmitPaymentInfo>) {
		return this.makeRequestWithThrow(Endpoint.SubmitPaymentInfo, input)
	}
	async submitPaymentInfoNoThrow(input: EndpointInput<Endpoint.SubmitPaymentInfo>) {
		return this.makeRequestNoThrow(Endpoint.SubmitPaymentInfo, input)
	}

	async cancelOrderPayment(input: EndpointInput<Endpoint.CancelOrderPayment>) {
		return this.makeRequestWithThrow(Endpoint.CancelOrderPayment, input)
	}
	async cancelOrderPaymentNoThrow(input: EndpointInput<Endpoint.CancelOrderPayment>) {
		return this.makeRequestNoThrow(Endpoint.CancelOrderPayment, input)
	}

	async validateOrder(input: EndpointInput<Endpoint.ValidateOrder>) {
		return this.makeRequestWithThrow(Endpoint.ValidateOrder, input)
	}
	async validateOrderNoThrow(input: EndpointInput<Endpoint.ValidateOrder>) {
		return this.makeRequestNoThrow(Endpoint.ValidateOrder, input)
	}

	async initiateOrderPayment(input: EndpointInput<Endpoint.InitiateOrderPayment>) {
		return this.makeRequestWithThrow(Endpoint.InitiateOrderPayment, input)
	}
	async initiateOrderPaymentNoThrow(input: EndpointInput<Endpoint.InitiateOrderPayment>) {
		return this.makeRequestNoThrow(Endpoint.InitiateOrderPayment, input)
	}

	async getOrderPaymentInfo(input: EndpointInput<Endpoint.GetOrderPaymentInfo>) {
		return this.makeRequestWithThrow(Endpoint.GetOrderPaymentInfo, input)
	}
	async getOrderPaymentInfoNoThrow(input: EndpointInput<Endpoint.GetOrderPaymentInfo>) {
		return this.makeRequestNoThrow(Endpoint.GetOrderPaymentInfo, input)
	}

	async getExitCode(input: EndpointInput<Endpoint.GetExitCode>) {
		return this.makeRequestWithThrow(Endpoint.GetExitCode, input)
	}
	async getExitCodeNoThrow(input: EndpointInput<Endpoint.GetExitCode>) {
		return this.makeRequestNoThrow(Endpoint.GetExitCode, input)
	}

	async getOrderReceipt(input: EndpointInput<Endpoint.GetOrderReceipt>) {
		return this.makeRequestWithThrow(Endpoint.GetOrderReceipt, input)
	}
	async getOrderReceiptNoThrow(input: EndpointInput<Endpoint.GetOrderReceipt>) {
		return this.makeRequestNoThrow(Endpoint.GetOrderReceipt, input)
	}

	async getOrderReceiptUrl(input: EndpointInput<Endpoint.GetOrderReceiptUrl>) {
		return this.makeRequestWithThrow(Endpoint.GetOrderReceiptUrl, input)
	}
	async getOrderReceiptUrlNoThrow(input: EndpointInput<Endpoint.GetOrderReceiptUrl>) {
		return this.makeRequestNoThrow(Endpoint.GetOrderReceiptUrl, input)
	}

	async getOrderReceiptPdf(input: EndpointInput<Endpoint.GetOrderReceiptPdf>) {
		return this.makeRequestWithThrow(Endpoint.GetOrderReceiptPdf, input)
	}
	async getOrderReceiptPdfNoThrow(input: EndpointInput<Endpoint.GetOrderReceiptPdf>) {
		return this.makeRequestNoThrow(Endpoint.GetOrderReceiptPdf, input)
	}

	async getProducts(input: EndpointInput<Endpoint.GetProducts>) {
		return this.makeRequestWithThrow(Endpoint.GetProducts, input)
	}
	async getProductsNoThrow(input: EndpointInput<Endpoint.GetProducts>) {
		return this.makeRequestNoThrow(Endpoint.GetProducts, input)
	}

	async getCategories(input: EndpointInput<Endpoint.GetCategories>) {
		return this.makeRequestWithThrow(Endpoint.GetCategories, input)
	}
	async getCategoriesNoThrow(input: EndpointInput<Endpoint.GetCategories>) {
		return this.makeRequestNoThrow(Endpoint.GetCategories, input)
	}

	async getProductsFromGroup(input: EndpointInput<Endpoint.GetProductsFromGroup>) {
		return this.makeRequestWithThrow(Endpoint.GetProductsFromGroup, input)
	}
	async getProductsFromGroupNoThrow(input: EndpointInput<Endpoint.GetProductsFromGroup>) {
		return this.makeRequestNoThrow(Endpoint.GetProductsFromGroup, input)
	}

	async getVariant(input: EndpointInput<Endpoint.GetVariant>) {
		return this.makeRequestWithThrow(Endpoint.GetVariant, input)
	}
	async getVariantNoThrow(input: EndpointInput<Endpoint.GetVariant>) {
		return this.makeRequestNoThrow(Endpoint.GetVariant, input)
	}

	async getVariantFromBarcode(input: EndpointInput<Endpoint.GetVariantFromBarcode>) {
		return this.makeRequestWithThrow(Endpoint.GetVariantFromBarcode, input)
	}
	async getVariantFromBarcodeNoThrow(input: EndpointInput<Endpoint.GetVariantFromBarcode>) {
		return this.makeRequestNoThrow(Endpoint.GetVariantFromBarcode, input)
	}
}
